import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { HttpStatusCode } from 'axios';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { toast } from 'react-toastify';
import { Box, Button, CircularProgress, TextField } from '@mui/material';
import styles from './building-sites-form.module.scss';
import { Country } from '../../../components/order/models';
import { BuildingSiteFormViewModel } from './building-sites-form-view.model';
import { IBuildingSiteDto } from './building-sites-form-dto.interface';
import { addBuildingSite, getEditedBuildingSite, htmlId, updateBuildingSite } from './building-sites-form.service';
import { BUILDING_SITES_PATHS, SHOPPING_CART_PATHS } from '../../../shared/constants/breadcrumb.constants';
import React from 'react';
import { BuildingSiteFormSkeleton } from './BuildingSiteFormSkeleton';
import { createBuildingSiteValidationSchema } from './building-sites-form.validation';
import { SearchableCountryField } from '../../../shared/country-select/SearchableCountryField';
import { useSelectBuildingSite } from './use-select-building-site';
import { IBuildingSiteListViewDto } from '../../../components/order/interfaces/building-site-list-view.interface';

export interface IContactUsFormInput {
    isEdit: boolean;
    readonly: boolean;
}

const rowSx = {
    display: 'grid',
    alignItems: 'center',
    columnGap: 3,
    mb: 2,
    mt: 2,
};

const BuildingSitesForm: React.FC<IContactUsFormInput> = ({ isEdit, readonly }) => {
    const { t } = useTranslation('translation');
    const [initialValues, setInitialValues] = useState<BuildingSiteFormViewModel>(new BuildingSiteFormViewModel());
    const { contractorUuid } = useParams();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isCreatedLoading, setIsCreatedLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const location = useLocation();
    const selectBuildingSite = useSelectBuildingSite();
    const cameFromCart = Boolean((location.state as { fromCart?: boolean } | null)?.fromCart);
    const showError = (message: string) => toast.error(message);

    const validationSchema = useMemo(() => createBuildingSiteValidationSchema(t), [t]);

    useEffect(() => {
        if (isEdit && contractorUuid) {
            handleEditedBulidingSite(contractorUuid);
        } else {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEditedBulidingSite = (contractorUuid: string) => {
        getEditedBuildingSite(contractorUuid).then((response) => {
            if (response) {
                const model: BuildingSiteFormViewModel = response.data;
                setInitialValues(model);
                setIsLoading(false);
            }
        });
    };

    const getModelDto = (values: BuildingSiteFormViewModel, code?: string): IBuildingSiteDto => {
        const dto: IBuildingSiteDto = {
            name: values.name,
            code: code,
            street: values.street,
            buildingNumber: values.buildingNumber,
            country: values.country,
            countryId: values.countryId,
            postalCode: values.postalCode,
            city: values.city,
            comment: values.comment,
            mobile: '',
            phone: '',
            email: '',
            firstName: '',
            lastName: '',
            contractorUuid: contractorUuid,
            contactId: initialValues.contactId,
            addressId: initialValues.addressId,
        };
        return dto;
    };

    const update = (values: BuildingSiteFormViewModel): void | Promise<any> => {
        setIsCreatedLoading(true);
        updateBuildingSite(getModelDto(values, values.code))
            .then((response) => {
                if (response && response.status === HttpStatusCode.Ok) {
                    setIsCreatedLoading(false);
                    navigate(BUILDING_SITES_PATHS[0].link);
                } else {
                    showError(t('commonErrors.SENDING_MESSAGE'));
                    setIsCreatedLoading(false);
                }
            })
            .catch(() => {
                setIsCreatedLoading(false);
                showError(t('commonErrors.SENDING_MESSAGE'));
            });
    };

    const add = (values: BuildingSiteFormViewModel): void | Promise<any> => {
        setIsCreatedLoading(true);
        addBuildingSite(getModelDto(values, ''))
            .then((response) => {
                if (response && response.status === HttpStatusCode.Ok) {
                    setIsCreatedLoading(false);
                    const created: IBuildingSiteDto = response.data;
                    if (cameFromCart && created.id) {
                        selectBuildingSite({
                            id: created.id,
                            name: created.name,
                            code: created.code,
                            country: created.country,
                        } as IBuildingSiteListViewDto);
                        navigate(SHOPPING_CART_PATHS[1].link);
                    } else {
                        navigate(BUILDING_SITES_PATHS[0].link);
                    }
                } else {
                    showError(t('commonErrors.SENDING_MESSAGE'));
                    setIsCreatedLoading(false);
                }
            })
            .catch(() => {
                setIsCreatedLoading(false);
                showError(t('commonErrors.SENDING_MESSAGE'));
            });
    };

    const formik = useFormik<BuildingSiteFormViewModel>({
        enableReinitialize: true,
        validateOnMount: true,
        initialValues,
        validationSchema,
        onSubmit: (values) => {
            !isEdit ? add(values) : update(values);
        },
    });

    const displayFillRequiredData = () => {
        formik.validateForm().then((errors) => {
            if (Object.keys(errors).length > 0) {
                showError(t('register.fillAllRequiredData'));
            }
        });
    };

    const errorText = (field: keyof BuildingSiteFormViewModel): string | undefined => {
        const error = formik.errors[field];
        return formik.touched[field] && error ? (error as string) : undefined;
    };

    const titleKey = readonly ? 'VIEW_BUILDING_SITE' : isEdit ? 'EDIT_BUILDING_SITE' : 'ADD_NEW_BUILDING_SITE';

    const handleCountrySelect = (country: Country) => {
        formik.setFieldValue('country', country.code);
        formik.setFieldValue('countryId', country.id).then(() => {
            formik.setFieldTouched('countryId', true, false);
        });
    };

    const handleCountryResolved = (country: Country) => {
        formik.setFieldValue('country', country.code);
    };

    const handleCountryBlur = () => {
        formik.setFieldTouched('countryId', true);
    };

    return (
        <div className={styles.form_container}>
            {isLoading ? (
                <BuildingSiteFormSkeleton />
            ) : (
                <React.Fragment>
                    <Box component="form" className={styles.form} onSubmit={formik.handleSubmit} autoComplete="off">
                        <div className={styles.form_content}>
                            <div className={styles.content}>
                                <label className={styles.main_header_text}>{t(titleKey)}</label>

                                <Box sx={rowSx}>
                                    <TextField
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        id={`${htmlId}-name-input`}
                                        name="name"
                                        label={t('BUILDING_SITE')}
                                        variant="outlined"
                                        size="small"
                                        value={formik.values.name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(errorText('name'))}
                                        helperText={errorText('name')}
                                        InputProps={{ readOnly: readonly }}
                                        className={readonly ? styles.readonly : ''}
                                    />
                                </Box>

                                {(readonly || isEdit) && (
                                    <Box sx={rowSx}>
                                        <TextField
                                            InputLabelProps={{ shrink: true }}
                                            fullWidth
                                            id={`${htmlId}-code-input`}
                                            name="code"
                                            label={t('CODE')}
                                            variant="outlined"
                                            size="small"
                                            value={formik.values.code}
                                            onChange={formik.handleChange}
                                            disabled
                                            className={styles.readonly}
                                        />
                                    </Box>
                                )}

                                <Box sx={rowSx}>
                                    <TextField
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        id={`${htmlId}-street-input`}
                                        name="street"
                                        label={t('STREET')}
                                        variant="outlined"
                                        size="small"
                                        value={formik.values.street}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(errorText('street'))}
                                        helperText={errorText('street')}
                                        InputProps={{ readOnly: readonly }}
                                        className={readonly ? styles.readonly : ''}
                                    />
                                </Box>

                                <Box sx={rowSx}>
                                    <TextField
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        id={`${htmlId}-building-number-input`}
                                        name="buildingNumber"
                                        label={t('BUILDING_NUMBER')}
                                        variant="outlined"
                                        size="small"
                                        value={formik.values.buildingNumber}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(errorText('buildingNumber'))}
                                        helperText={errorText('buildingNumber')}
                                        InputProps={{ readOnly: readonly }}
                                        className={readonly ? styles.readonly : ''}
                                    />
                                </Box>

                                <Box sx={rowSx}>
                                    <TextField
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        id={`${htmlId}-zip-input`}
                                        name="postalCode"
                                        label={t('ZIP')}
                                        variant="outlined"
                                        size="small"
                                        value={formik.values.postalCode}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(errorText('postalCode'))}
                                        helperText={errorText('postalCode')}
                                        InputProps={{ readOnly: readonly }}
                                        className={readonly ? styles.readonly : ''}
                                    />
                                </Box>

                                <Box sx={rowSx}>
                                    <TextField
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        id={`${htmlId}-city-input`}
                                        name="city"
                                        label={t('CITY')}
                                        variant="outlined"
                                        size="small"
                                        value={formik.values.city}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(errorText('city'))}
                                        helperText={errorText('city')}
                                        InputProps={{ readOnly: readonly }}
                                        className={readonly ? styles.readonly : ''}
                                    />
                                </Box>

                                <Box sx={rowSx}>
                                    <SearchableCountryField
                                        id={`${htmlId}-country-code-select`}
                                        name="countryId"
                                        label={t('COUNTRY')}
                                        value={formik.values.countryId}
                                        onSelect={handleCountrySelect}
                                        onValueResolved={handleCountryResolved}
                                        onBlur={handleCountryBlur}
                                        error={Boolean(errorText('countryId'))}
                                        helperText={errorText('countryId')}
                                        readOnly={readonly}
                                        className={readonly ? styles.readonly : ''}
                                    />
                                </Box>

                                <Box sx={rowSx}>
                                    <TextField
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        multiline
                                        rows={4}
                                        id={`${htmlId}-comment-input`}
                                        name="comment"
                                        label={t('COMMENTS')}
                                        variant="outlined"
                                        size="small"
                                        value={formik.values.comment}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(errorText('comment'))}
                                        helperText={errorText('comment')}
                                        InputProps={{ readOnly: readonly }}
                                        className={readonly ? styles.readonly : ''}
                                    />
                                </Box>

                                <div className={styles.button_content}>
                                    {!readonly && (
                                        <Button
                                            className={styles.send_btn}
                                            variant="contained"
                                            type="submit"
                                            fullWidth
                                            id={`${htmlId}-submit-btn`}
                                            disabled={readonly || isCreatedLoading}
                                            onClick={displayFillRequiredData}
                                            sx={{
                                                height: 42,
                                                backgroundColor: '#821818',
                                                color: '#fff',
                                                textTransform: 'none',
                                                boxShadow: 'none',
                                                '&:hover': {
                                                    backgroundColor: '#821818',
                                                    boxShadow: 'none',
                                                },
                                                '&:active': {
                                                    boxShadow: 'none',
                                                },
                                                '&:focus': {
                                                    outline: 'none',
                                                },
                                            }}
                                            disableRipple
                                        >
                                            {isCreatedLoading ? (
                                                <CircularProgress size={22} color="inherit" />
                                            ) : (
                                                t('SEND')
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Box>
                </React.Fragment>
            )}
        </div>
    );
};

export default BuildingSitesForm;
